import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Code as CodeIcon,
  Storage as DatabaseIcon,
  Cloud as CloudIcon,
  Language as FrontendIcon,
  Settings as BackendIcon,
  Security as SecurityIcon,
  PhoneAndroid as MobileIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';

const TechnologyStack = ({ projectId, existingContent = '', onSave }) => {
  const renderTechStackPreview = (content) => {
    // Try to parse as JSON first
    try {
      const techData = JSON.parse(content);
      return renderStructuredTechStack(techData);
    } catch (e) {
      // If not JSON, render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render a structured tech stack visualization
  const renderStructuredTechStack = (techData) => {
    const categories = {
      frontend: { icon: <FrontendIcon color="primary" />, title: "Frontend" },
      backend: { icon: <BackendIcon color="primary" />, title: "Backend" },
      database: { icon: <DatabaseIcon color="primary" />, title: "Database" },
      devops: { icon: <CloudIcon color="primary" />, title: "DevOps/Cloud" },
      mobile: { icon: <MobileIcon color="primary" />, title: "Mobile" },
      security: { icon: <SecurityIcon color="primary" />, title: "Security" },
      testing: { icon: <DevicesIcon color="primary" />, title: "Testing" },
    };
    
    return (
      <Box>
        <Grid container spacing={3}>
          {Object.entries(categories).map(([key, value]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {value.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>{value.title}</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {techData && techData[key] ? (
                    <List dense>
                      {techData[key].map((tech, index) => (
                        <ListItem key={index}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CodeIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={tech.name || tech} 
                            secondary={tech.description || ''} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No {value.title.toLowerCase()} technologies specified
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  
  // These would typically come from a config file or API
  const techStackPromptConfig = {
    system: "You are a technology consultant specialized in helping teams choose the right tech stack for their projects.",
    examples: [
      "What's the best frontend framework for a financial dashboard?",
      "Recommend a database for high-volume time-series data"
    ],
    instruction: "Provide detailed technology recommendations based on the project's requirements."
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="techStack"
      title="Technology Stack"
      icon={CodeIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderTechStackPreview}
      promptConfig={techStackPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown"
    >
      {/* Additional customization content goes here */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Technology Recommendations</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>Popular Frontend Frameworks</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="React" />
              <Chip label="Vue.js" />
              <Chip label="Angular" />
              <Chip label="Next.js" />
              <Chip label="Svelte" />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>Popular Backend Frameworks</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="Node.js/Express" />
              <Chip label="Django" />
              <Chip label="Ruby on Rails" />
              <Chip label="Spring Boot" />
              <Chip label="Laravel" />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>Popular Databases</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="PostgreSQL" />
              <Chip label="MongoDB" />
              <Chip label="MySQL" />
              <Chip label="Redis" />
              <Chip label="Firebase" />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>DevOps & Cloud</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="AWS" />
              <Chip label="Google Cloud" />
              <Chip label="Azure" />
              <Chip label="Docker" />
              <Chip label="Kubernetes" />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </DocumentTypeTemplate>
  );
};

export default TechnologyStack;